/**
 * 3D visualization of a floor plan — projection, scene building and shading.
 *
 * Everything here is pure geometry so the whole renderer is unit-testable and
 * carries no dependency: the plan is drawn as SVG polygons by a small software
 * renderer rather than through WebGL. The canvas the producer edits is already
 * SVG, so the 3D view shares its units (feet), its item model (`CanvasItem`)
 * and its chair math (`calculateRoundTableChairs` and friends) — a table with
 * eight chairs looks the same in both views because both ask the same helper
 * where the chairs go.
 *
 * ─── Coordinate systems ──────────────────────────────────────────────────
 *
 * World: `x` runs right and `y` runs "down" the plan (both in feet, matching
 * the 2D canvas once `pxToFt` has been applied), and `z` is height above the
 * floor. Camera: `x` right, `y` up, `z` into the screen (a right-handed basis,
 * so cross products behave normally and back-face culling is a sign test).
 *
 * The camera orbits a point on the floor. `pitch: 90` looks straight down and
 * reproduces the 2D plan exactly — same orientation, nothing mirrored — so
 * tilting away from the plan never disorients the person who drew it.
 */

import {
  calculateChairRowGroup,
  calculateRectTableChairs,
  calculateRoundTableChairs,
  type ChairSides,
  type PerimeterPoint,
} from '@peles/shared';
import { shadeHexColor } from '../../utils/color';
import { CHAIR_SIZE_FT, chairsOf, isRoundParent, pxToFt } from './helpers';
import type { CanvasItem } from './types';
import { isTableItem } from './types';

export interface Point2 {
  x: number;
  y: number;
}

export interface Point3 extends Point2 {
  z: number;
}

export interface Viewport {
  width: number;
  height: number;
}

export interface Camera3D {
  /** Orbit angle about the vertical axis, in degrees. */
  yaw: number;
  /** Elevation, in degrees. 0 = eye level, 90 = straight down (the 2D plan). */
  pitch: number;
  /** Eye distance from the orbit target, in feet. */
  distanceFt: number;
  /** The floor point the camera orbits, in feet. */
  target: Point2;
  /** Screen pixels per foot at the target plane. */
  pxPerFt: number;
  /** Foreshorten with distance. False renders an orthographic ("plan") view. */
  perspective: boolean;
}

export const MIN_PITCH = 5;
export const MAX_PITCH = 90;

export const DEFAULT_CAMERA: Omit<Camera3D, 'target' | 'distanceFt' | 'pxPerFt'> = {
  yaw: 0,
  pitch: 42,
  perspective: true,
};

/** Keep pitch inside the range where the floor is still readable. */
export function clampPitch(pitch: number): number {
  return Math.min(MAX_PITCH, Math.max(MIN_PITCH, pitch));
}

/** Normalize a yaw to [0, 360). */
export function normalizeYaw(yaw: number): number {
  return ((yaw % 360) + 360) % 360;
}

// ─── Projection ─────────────────────────────────────────────────────────────

export interface CameraSpacePoint {
  x: number;
  y: number;
  /** Depth along the view direction; larger is farther from the eye. */
  z: number;
}

export interface ProjectedPoint extends Point2 {
  depth: number;
  /** True when the point sits behind the eye and its position is clamped. */
  behind: boolean;
}

/** World point → camera space (right / up / into-screen). */
export function toCameraSpace(point: Point3, camera: Camera3D): CameraSpacePoint {
  const yaw = (camera.yaw * Math.PI) / 180;
  const pitch = (clampPitch(camera.pitch) * Math.PI) / 180;
  const dx = point.x - camera.target.x;
  const dy = point.y - camera.target.y;
  const xr = dx * Math.cos(yaw) - dy * Math.sin(yaw);
  const yr = dx * Math.sin(yaw) + dy * Math.cos(yaw);
  const sinP = Math.sin(pitch);
  const cosP = Math.cos(pitch);
  return {
    x: xr,
    y: -yr * sinP + point.z * cosP,
    z: -yr * cosP - point.z * sinP,
  };
}

/** Where the eye sits in world feet — used to decide which walls to cut away. */
export function eyePosition(camera: Camera3D): Point3 {
  const yaw = (camera.yaw * Math.PI) / 180;
  const pitch = (clampPitch(camera.pitch) * Math.PI) / 180;
  const ground = camera.distanceFt * Math.cos(pitch);
  return {
    x: camera.target.x + ground * Math.sin(yaw),
    y: camera.target.y + ground * Math.cos(yaw),
    z: camera.distanceFt * Math.sin(pitch),
  };
}

/** Camera-space point → screen pixels, with the perspective divide. */
export function projectCameraPoint(
  point: CameraSpacePoint,
  camera: Camera3D,
  viewport: Viewport,
): ProjectedPoint {
  const eyeDistance = camera.distanceFt + point.z;
  const minDistance = Math.max(camera.distanceFt * 0.05, 0.001);
  const behind = eyeDistance < minDistance;
  const scale = camera.perspective
    ? (camera.pxPerFt * camera.distanceFt) / Math.max(eyeDistance, minDistance)
    : camera.pxPerFt;
  return {
    x: viewport.width / 2 + point.x * scale,
    y: viewport.height / 2 - point.y * scale,
    depth: point.z,
    behind,
  };
}

/** World point → screen pixels. */
export function project(point: Point3, camera: Camera3D, viewport: Viewport): ProjectedPoint {
  return projectCameraPoint(toCameraSpace(point, camera), camera, viewport);
}

// ─── Shading ────────────────────────────────────────────────────────────────

/** Direction the key light comes from (world space, normalized). */
const LIGHT: Point3 = { x: -0.32, y: -0.45, z: 0.83 };
const AMBIENT = 0.55;

function normalize(v: Point3): Point3 {
  const len = Math.hypot(v.x, v.y, v.z);
  if (len === 0) return { x: 0, y: 0, z: 0 };
  return { x: v.x / len, y: v.y / len, z: v.z / len };
}

function cross(a: Point3, b: Point3): Point3 {
  return {
    x: a.y * b.z - a.z * b.y,
    y: a.z * b.x - a.x * b.z,
    z: a.x * b.y - a.y * b.x,
  };
}

function dot(a: Point3, b: Point3): number {
  return a.x * b.x + a.y * b.y + a.z * b.z;
}

function subtract(a: Point3, b: Point3): Point3 {
  return { x: a.x - b.x, y: a.y - b.y, z: a.z - b.z };
}

/** Lambert term for a world-space face normal, in [AMBIENT, 1]. */
export function faceBrightness(normalVec: Point3): number {
  const n = normalize(normalVec);
  return AMBIENT + (1 - AMBIENT) * Math.max(0, dot(n, LIGHT));
}

/** Apply the lighting term to a base color. */
export function shadeFace(color: string, brightness: number): string {
  return shadeHexColor(color, Math.round((brightness - 1) * 100));
}

// ─── Solids ─────────────────────────────────────────────────────────────────

/**
 * One extruded prism: a footprint in world feet lifted from `baseZ` to `topZ`.
 * Every object in the plan becomes one or more of these — a table is a
 * pedestal plus a top slab, a chuppah is four posts plus a canopy.
 */
export interface Solid3 {
  key: string;
  footprint: Point2[];
  baseZ: number;
  topZ: number;
  color: string;
  opacity: number;
  /** Skip the side faces whose outward normal points at the eye (walls). */
  cutawayNearSide?: boolean;
  /**
   * Omit the top cap. A table pedestal ends flush under its own top and a
   * chuppah post under its canopy, so their caps are never actually seen —
   * and drawing them lets a nearer cap sort over the surface covering it.
   */
  skipTop?: boolean;
}

export interface SceneLabel {
  key: string;
  text: string;
  /** Anchor point in world feet — the label floats above it. */
  anchor: Point3;
}

export interface Scene3D {
  /** Room outline at floor level, in world feet. */
  floor: Point2[];
  /** Grid line segments across the floor, in world feet. */
  grid: Array<[Point2, Point2]>;
  solids: Solid3[];
  labels: SceneLabel[];
  /** Bounding box of everything in the scene, for camera fitting. */
  bounds: SceneBounds;
}

export interface SceneBounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
  /**
   * Height of the tallest thing standing in the scene, in feet. The camera
   * has to clear it: a nine-foot pillar in a small room reaches past an eye
   * placed on the floor plan's width alone, and the faces that cross the
   * near plane are dropped — the pillar goes missing rather than looking
   * wrong, which is harder to notice.
   */
  maxZ?: number;
}

/** Shoelace area; positive means the ring is wound counter-clockwise in (x, y). */
export function signedArea(points: Point2[]): number {
  let sum = 0;
  for (let i = 0; i < points.length; i++) {
    const a = points[i];
    const b = points[(i + 1) % points.length];
    sum += a.x * b.y - b.x * a.y;
  }
  return sum / 2;
}

/**
 * Wind a footprint positively so "outward" is unambiguous: every side face is
 * then emitted with its outward normal, and culling is a single sign test.
 */
export function orientFootprint(points: Point2[]): Point2[] {
  return signedArea(points) < 0 ? [...points].reverse() : points;
}

export interface Face3 {
  key: string;
  points: Point3[];
  color: string;
  opacity: number;
  /** True for the top cap — drawn slightly brighter and with a softer edge. */
  isTop: boolean;
}

/**
 * Expand a solid into its visible faces: the top cap plus one quad per edge.
 * The bottom cap is never emitted — solids rest on the floor, and a floating
 * one (a chuppah canopy) is only ever seen from above the room.
 */
export function solidToFaces(solid: Solid3, eye?: Point3): Face3[] {
  const ring = orientFootprint(solid.footprint);
  if (ring.length < 3) return [];
  const faces: Face3[] = solid.skipTop
    ? []
    : [
        {
          key: `${solid.key}:top`,
          points: ring.map((p) => ({ x: p.x, y: p.y, z: solid.topZ })),
          color: solid.color,
          opacity: solid.opacity,
          isTop: true,
        },
      ];
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i];
    const b = ring[(i + 1) % ring.length];
    if (solid.cutawayNearSide && eye) {
      // Outward normal of a positively-wound ring is to the right of the edge.
      const outward = { x: b.y - a.y, y: -(b.x - a.x), z: 0 };
      const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2, z: solid.baseZ };
      // A wall between the eye and the room would hide everything behind it,
      // so the near side is cut away and the room is seen from outside-in.
      if (dot(outward, subtract(eye, mid)) > 0) continue;
    }
    faces.push({
      key: `${solid.key}:side${i}`,
      points: [
        { x: a.x, y: a.y, z: solid.baseZ },
        { x: b.x, y: b.y, z: solid.baseZ },
        { x: b.x, y: b.y, z: solid.topZ },
        { x: a.x, y: a.y, z: solid.topZ },
      ],
      color: solid.color,
      opacity: solid.opacity,
      isTop: false,
    });
  }
  return faces;
}

// ─── Object heights ─────────────────────────────────────────────────────────

export type Solid3Kind =
  /** A plain extrusion of the footprint. */
  | 'BOX'
  /** A floor marking — dance floor, aisle, door swing. */
  | 'SLAB'
  /** A top on a narrower pedestal. */
  | 'TABLE'
  /** A seat with a back. */
  | 'CHAIR'
  /** Corner posts carrying a canopy — chuppah, arch. */
  | 'FRAME';

export interface Object3DProfile {
  heightFt: number;
  kind: Solid3Kind;
}

/**
 * How tall each library object stands. These are the defaults a plan renders
 * with; an individual object overrides its own height through
 * `config.heightFt3d`, which the properties panel writes.
 */
export const OBJECT_3D_PROFILES: Record<string, Object3DProfile> = {
  // Tables
  TABLE: { heightFt: 2.5, kind: 'TABLE' },
  TABLE_ROUND: { heightFt: 2.5, kind: 'TABLE' },
  TABLE_OVAL: { heightFt: 2.5, kind: 'TABLE' },
  TABLE_SQUARE: { heightFt: 2.5, kind: 'TABLE' },
  TABLE_RECT: { heightFt: 2.5, kind: 'TABLE' },
  TABLE_HEAD: { heightFt: 2.5, kind: 'TABLE' },
  TABLE_SERPENTINE: { heightFt: 2.5, kind: 'TABLE' },
  SWEETHEART_TABLE: { heightFt: 2.5, kind: 'TABLE' },
  DESSERT_TABLE: { heightFt: 2.5, kind: 'TABLE' },
  BUFFET: { heightFt: 3, kind: 'TABLE' },
  COCKTAIL_TABLE: { heightFt: 3.5, kind: 'TABLE' },
  COFFEE_TABLE: { heightFt: 1.4, kind: 'TABLE' },
  // Seating
  CHAIR: { heightFt: 1.5, kind: 'CHAIR' },
  ARMCHAIR: { heightFt: 2.6, kind: 'BOX' },
  SOFA: { heightFt: 2.8, kind: 'BOX' },
  LOVESEAT: { heightFt: 2.8, kind: 'BOX' },
  OTTOMAN: { heightFt: 1.4, kind: 'BOX' },
  // Structures & staging
  STAGE: { heightFt: 1.5, kind: 'BOX' },
  DANCE_FLOOR: { heightFt: 0.15, kind: 'SLAB' },
  BAR: { heightFt: 3.7, kind: 'BOX' },
  DJ_BOOTH: { heightFt: 3.7, kind: 'BOX' },
  PHOTO_BOOTH: { heightFt: 7, kind: 'BOX' },
  PHOTO_WALL: { heightFt: 8, kind: 'BOX' },
  PODIUM: { heightFt: 4, kind: 'BOX' },
  PIANO: { heightFt: 3.3, kind: 'BOX' },
  SPEAKER: { heightFt: 5, kind: 'BOX' },
  PROJECTOR_SCREEN: { heightFt: 7, kind: 'BOX' },
  STAIRS: { heightFt: 2, kind: 'BOX' },
  PARTITION: { heightFt: 6, kind: 'BOX' },
  MECHITZA: { heightFt: 6, kind: 'BOX' },
  WALL_SEGMENT: { heightFt: 8, kind: 'BOX' },
  COLUMN: { heightFt: 9, kind: 'BOX' },
  PILLAR: { heightFt: 9, kind: 'BOX' },
  // Ceremony & décor
  CHUPPAH: { heightFt: 8, kind: 'FRAME' },
  ARCH: { heightFt: 8, kind: 'FRAME' },
  PLANT_FLORAL: { heightFt: 4, kind: 'BOX' },
  CANDLE_LIGHT: { heightFt: 3, kind: 'BOX' },
  // Floor markings
  AISLE: { heightFt: 0.05, kind: 'SLAB' },
  AREA: { heightFt: 0.05, kind: 'SLAB' },
  WINDOW: { heightFt: 0.05, kind: 'SLAB' },
};

const SLAB_PROFILE: Object3DProfile = { heightFt: 0.05, kind: 'SLAB' };
const TABLE_PROFILE: Object3DProfile = { heightFt: 2.5, kind: 'TABLE' };
const DEFAULT_PROFILE: Object3DProfile = { heightFt: 3, kind: 'BOX' };

/**
 * The profile for an item. Custom objects from Settings are not in the table
 * above, so they fall back on what the plan already knows about them: a table
 * stands at table height, background art stays flat on the floor, and anything
 * else is a waist-high box.
 */
export function resolveProfile(item: CanvasItem): Object3DProfile {
  const listed = OBJECT_3D_PROFILES[item.objectType];
  const base =
    listed ??
    (isTableItem(item) || item.config?.isTable
      ? TABLE_PROFILE
      : item.objectType.startsWith('DOOR_') || item.config?.isBackground
        ? SLAB_PROFILE
        : DEFAULT_PROFILE);
  const override = item.config?.heightFt3d;
  if (typeof override === 'number' && Number.isFinite(override) && override > 0) {
    return { ...base, heightFt: override };
  }
  return base;
}

// ─── Footprints ─────────────────────────────────────────────────────────────

const ELLIPSE_SEGMENTS = 28;

function rotatePoint(p: Point2, center: Point2, degrees: number): Point2 {
  if (!degrees) return p;
  const rad = (degrees * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const dx = p.x - center.x;
  const dy = p.y - center.y;
  return { x: center.x + dx * cos - dy * sin, y: center.y + dx * sin + dy * cos };
}

/** Local outline (centered on the origin) for an item's shape, in feet. */
function localOutline(item: CanvasItem): Point2[] {
  const hw = item.widthFt / 2;
  const hh = item.heightFt / 2;
  const shape = String(item.config?.shape ?? '');
  if (isRoundParent(item) || shape === 'CIRCLE' || shape === 'OVAL') {
    return Array.from({ length: ELLIPSE_SEGMENTS }, (_, i) => {
      const angle = (2 * Math.PI * i) / ELLIPSE_SEGMENTS;
      return { x: hw * Math.cos(angle), y: hh * Math.sin(angle) };
    });
  }
  if (shape === 'DIAMOND') {
    return [
      { x: 0, y: -hh },
      { x: hw, y: 0 },
      { x: 0, y: hh },
      { x: -hw, y: 0 },
    ];
  }
  if (shape === 'HEXAGON') {
    return Array.from({ length: 6 }, (_, i) => {
      const angle = (Math.PI / 6) * (2 * i + 1);
      return { x: hw * Math.cos(angle), y: hh * Math.sin(angle) };
    });
  }
  return [
    { x: -hw, y: -hh },
    { x: hw, y: -hh },
    { x: hw, y: hh },
    { x: -hw, y: hh },
  ];
}

/** Center of an item in world feet (its stored x/y are canvas pixels). */
export function itemCenterFt(item: CanvasItem): Point2 {
  return {
    x: pxToFt(item.x) + item.widthFt / 2,
    y: pxToFt(item.y) + item.heightFt / 2,
  };
}

/** An item's outline in world feet, rotated about its own center. */
export function itemFootprint(item: CanvasItem, inset = 0): Point2[] {
  const center = itemCenterFt(item);
  const scaleX = item.widthFt > 0 ? Math.max(0, item.widthFt - inset * 2) / item.widthFt : 1;
  const scaleY = item.heightFt > 0 ? Math.max(0, item.heightFt - inset * 2) / item.heightFt : 1;
  return localOutline(item).map((p) =>
    rotatePoint(
      { x: center.x + p.x * scaleX, y: center.y + p.y * scaleY },
      center,
      item.rotation || 0,
    ),
  );
}

/** A rectangle in world feet, rotated about `center`. */
function rectFootprint(
  center: Point2,
  widthFt: number,
  heightFt: number,
  rotationDeg: number,
): Point2[] {
  const hw = widthFt / 2;
  const hh = heightFt / 2;
  return [
    { x: center.x - hw, y: center.y - hh },
    { x: center.x + hw, y: center.y - hh },
    { x: center.x + hw, y: center.y + hh },
    { x: center.x - hw, y: center.y + hh },
  ].map((p) => rotatePoint(p, center, rotationDeg));
}

// ─── Scene building ─────────────────────────────────────────────────────────

const CHAIR_SEAT_FT = 1.5;
const CHAIR_BACK_FT = 3;
const CHAIR_BACK_THICKNESS_FT = 0.18;
const FRAME_POST_FT = 0.5;
const CANOPY_THICKNESS_FT = 0.35;

export interface BuildSceneOptions {
  items: CanvasItem[];
  /** Room the plan sits in — its outline becomes the floor and the walls. */
  room?: {
    widthFt: number;
    heightFt: number;
    perimeterPoints?: PerimeterPoint[] | null;
  } | null;
  /** Floor size when the plan is not attached to a room. */
  fallbackWidthFt?: number;
  fallbackHeightFt?: number;
  /** Draw the chairs a table seats, the same ones the 2D preview shows. */
  showChairs?: boolean;
  /** Height of the room walls, in feet. 0 leaves the room open. */
  wallHeightFt?: number;
  /** Table-number labels floating above each table. */
  showLabels?: boolean;
  /** Grid spacing in feet; 0 draws no grid. */
  gridFt?: number;
  /** Chair layout for rectangular tables — mirrors the 2D canvas setting. */
  chairSides?: ChairSides;
  chairSizeFt?: number;
}

const CHAIR_COLOR_3D = '#b8bec7';
const WALL_COLOR = '#e5e7eb';

function chairSolids(
  key: string,
  center: Point2,
  rotationDeg: number,
  sizeFt: number,
  color: string,
): Solid3[] {
  const seat = rectFootprint(center, sizeFt, sizeFt, rotationDeg);
  // The back sits along the chair's local far edge and rotates with the seat,
  // so a chair pulled up to a table always faces it.
  const backCenter = rotatePoint(
    { x: center.x, y: center.y - sizeFt / 2 + CHAIR_BACK_THICKNESS_FT / 2 },
    center,
    rotationDeg,
  );
  return [
    {
      key: `${key}:seat`,
      footprint: seat,
      baseZ: CHAIR_SEAT_FT - 0.25,
      topZ: CHAIR_SEAT_FT,
      color,
      opacity: 1,
    },
    {
      key: `${key}:back`,
      footprint: rectFootprint(backCenter, sizeFt, CHAIR_BACK_THICKNESS_FT, rotationDeg),
      baseZ: CHAIR_SEAT_FT,
      topZ: CHAIR_BACK_FT,
      color,
      opacity: 1,
    },
    {
      key: `${key}:leg`,
      footprint: rectFootprint(center, sizeFt * 0.45, sizeFt * 0.45, rotationDeg),
      baseZ: 0,
      topZ: CHAIR_SEAT_FT - 0.25,
      color: shadeHexColor(color, -12),
      opacity: 1,
      skipTop: true,
    },
  ];
}

/** The solids one canvas item contributes. */
export function itemSolids(item: CanvasItem, options: BuildSceneOptions = { items: [] }): Solid3[] {
  const profile = resolveProfile(item);
  const key = item.uid || item.id || `${item.objectType}-${item.sortOrder}`;
  const color = item.color || '#d1d5db';
  const opacity = item.opacity ?? 1;
  const center = itemCenterFt(item);
  const rotation = item.rotation || 0;
  const chairSizeFt = options.chairSizeFt ?? CHAIR_SIZE_FT;

  // A chair row is a block of seats, not a slab — expand it into real chairs
  // so a ceremony reads as rows of chairs from every angle.
  if (item.objectType === 'CHAIR_ROW') {
    const config = item.config ?? {};
    const rows = Number(config.rows) || 1;
    const seatsPerRow = Number(config.seatsPerRow) || Math.max(1, item.seats);
    const { chairs } = calculateChairRowGroup(
      rows,
      seatsPerRow,
      Number(config.chairSpacingFt) || 2,
      Number(config.rowSpacingFt) || 3,
      chairSizeFt,
    );
    const originX = pxToFt(item.x);
    const originY = pxToFt(item.y);
    return chairs.flatMap((chair, i) =>
      chairSolids(
        `${key}:row${i}`,
        rotatePoint(
          { x: originX + chair.x + chairSizeFt / 2, y: originY + chair.y + chairSizeFt / 2 },
          center,
          rotation,
        ),
        rotation,
        chairSizeFt,
        color,
      ),
    );
  }

  if (profile.kind === 'CHAIR') {
    return chairSolids(key, center, rotation, Math.max(item.widthFt, chairSizeFt), color);
  }

  if (profile.kind === 'SLAB') {
    return [
      {
        key,
        footprint: itemFootprint(item),
        baseZ: 0,
        topZ: Math.max(profile.heightFt, 0.02),
        color,
        opacity,
      },
    ];
  }

  if (profile.kind === 'TABLE') {
    const topThickness = Math.min(0.25, profile.heightFt / 4);
    const pedestalInset = Math.min(item.widthFt, item.heightFt) * 0.28;
    return [
      {
        key: `${key}:pedestal`,
        footprint: itemFootprint(item, pedestalInset),
        baseZ: 0,
        topZ: profile.heightFt - topThickness,
        color: shadeHexColor(color, -14),
        opacity,
        skipTop: true,
      },
      {
        key: `${key}:top`,
        footprint: itemFootprint(item),
        baseZ: profile.heightFt - topThickness,
        topZ: profile.heightFt,
        color,
        opacity,
      },
    ];
  }

  if (profile.kind === 'FRAME') {
    const postInset = FRAME_POST_FT / 2;
    const hw = item.widthFt / 2 - postInset;
    const hh = item.heightFt / 2 - postInset;
    const posts: Solid3[] = [
      { x: -hw, y: -hh },
      { x: hw, y: -hh },
      { x: hw, y: hh },
      { x: -hw, y: hh },
    ].map((offset, i) => ({
      key: `${key}:post${i}`,
      footprint: rectFootprint(
        rotatePoint({ x: center.x + offset.x, y: center.y + offset.y }, center, rotation),
        FRAME_POST_FT,
        FRAME_POST_FT,
        rotation,
      ),
      baseZ: 0,
      topZ: profile.heightFt - CANOPY_THICKNESS_FT,
      color: shadeHexColor(color, -18),
      opacity,
      // The canopy covers every post top.
      skipTop: true,
    }));
    return [
      ...posts,
      {
        key: `${key}:canopy`,
        footprint: itemFootprint(item),
        baseZ: profile.heightFt - CANOPY_THICKNESS_FT,
        topZ: profile.heightFt,
        color,
        opacity,
      },
    ];
  }

  return [
    {
      key,
      footprint: itemFootprint(item),
      baseZ: 0,
      topZ: profile.heightFt,
      color,
      opacity,
    },
  ];
}

/**
 * Chairs around a seated table, positioned by the same helpers the 2D canvas
 * uses — so turning the plan does not move a single chair.
 *
 * These are drawn only for a table nobody has placed chairs around. A producer
 * who has run "add chairs" has real `CHAIR` items saved with the plan, laid out
 * with the sides they chose; those are objects like any other and are already
 * drawn as themselves, so generating a second set puts every seat in the room
 * twice.
 */
export function tableChairSolids(item: CanvasItem, options: BuildSceneOptions): Solid3[] {
  if (!isTableItem(item) || item.seats <= 0) return [];
  if (chairsOf(options.items, item).length > 0) return [];
  const chairSizeFt = options.chairSizeFt ?? CHAIR_SIZE_FT;
  const round = isRoundParent(item);
  const positions = round
    ? calculateRoundTableChairs(Math.max(item.widthFt, item.heightFt), item.seats, chairSizeFt)
    : calculateRectTableChairs(
        item.widthFt,
        item.heightFt,
        item.seats,
        chairSizeFt,
        options.chairSides ?? 'top-bottom',
      );
  const center = itemCenterFt(item);
  const originX = pxToFt(item.x);
  const originY = pxToFt(item.y);
  const key = item.uid || item.id || `table-${item.tableNumber}`;
  return positions.flatMap((pos, i) => {
    // Round positions are relative to the table center; rectangular ones are
    // relative to its top-left corner (and name the chair's own top-left).
    const local = round
      ? { x: center.x + pos.x, y: center.y + pos.y }
      : {
          x: originX + pos.x + chairSizeFt / 2,
          y: originY + pos.y + chairSizeFt / 2,
        };
    return chairSolids(
      `${key}:chair${i}`,
      rotatePoint(local, center, item.rotation || 0),
      (item.rotation || 0) + pos.rotation,
      chairSizeFt,
      CHAIR_COLOR_3D,
    );
  });
}

/** The floor outline in world feet — the room's perimeter, or its rectangle. */
export function floorOutline(options: BuildSceneOptions): Point2[] {
  const { room } = options;
  if (room?.perimeterPoints && room.perimeterPoints.length >= 3) {
    return room.perimeterPoints.map((p) => ({ x: p.x, y: p.y }));
  }
  const widthFt = room?.widthFt ?? options.fallbackWidthFt ?? 60;
  const heightFt = room?.heightFt ?? options.fallbackHeightFt ?? 40;
  return [
    { x: 0, y: 0 },
    { x: widthFt, y: 0 },
    { x: widthFt, y: heightFt },
    { x: 0, y: heightFt },
  ];
}

function boundsOf(points: Point2[]): SceneBounds {
  if (points.length === 0) return { minX: 0, minY: 0, maxX: 1, maxY: 1 };
  return points.reduce(
    (acc, p) => ({
      minX: Math.min(acc.minX, p.x),
      minY: Math.min(acc.minY, p.y),
      maxX: Math.max(acc.maxX, p.x),
      maxY: Math.max(acc.maxY, p.y),
    }),
    { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity },
  );
}

/** A wall segment: a thin band running along the edge, inside the room. */
function wallFootprint(a: Point2, b: Point2, thicknessFt = 0.5): Point2[] {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len = Math.hypot(dx, dy) || 1;
  // Inward normal of a positively-wound ring points left of the edge.
  const nx = (-dy / len) * thicknessFt;
  const ny = (dx / len) * thicknessFt;
  return [a, b, { x: b.x + nx, y: b.y + ny }, { x: a.x + nx, y: a.y + ny }];
}

/** Turn a plan into the solids, floor, grid and labels a camera can render. */
export function buildScene(options: BuildSceneOptions): Scene3D {
  const floor = floorOutline(options);
  const showChairs = options.showChairs ?? true;
  const wallHeightFt = options.wallHeightFt ?? 0;

  const solids: Solid3[] = [];
  const labels: SceneLabel[] = [];

  if (wallHeightFt > 0) {
    const ring = orientFootprint(floor);
    for (let i = 0; i < ring.length; i++) {
      const a = ring[i];
      const b = ring[(i + 1) % ring.length];
      solids.push({
        key: `wall${i}`,
        footprint: wallFootprint(a, b),
        baseZ: 0,
        topZ: wallHeightFt,
        color: WALL_COLOR,
        opacity: 1,
        cutawayNearSide: true,
      });
    }
  }

  const ordered = [...options.items].sort((a, b) => a.sortOrder - b.sortOrder);
  ordered.forEach((item) => {
    solids.push(...itemSolids(item, options));
    if (showChairs) solids.push(...tableChairSolids(item, options));
    if (options.showLabels && isTableItem(item) && item.tableNumber !== 0) {
      const center = itemCenterFt(item);
      labels.push({
        key: `label:${item.uid || item.id || item.tableNumber}`,
        text: item.label || `${item.tableNumber}`,
        anchor: { x: center.x, y: center.y, z: resolveProfile(item).heightFt + 1.6 },
      });
    }
  });

  const gridFt = options.gridFt ?? 5;
  const bounds = {
    ...boundsOf([...floor, ...solids.flatMap((s) => s.footprint)]),
    maxZ: solids.reduce((tallest, solid) => Math.max(tallest, solid.topZ), 0),
  };
  const grid: Array<[Point2, Point2]> = [];
  if (gridFt > 0) {
    const floorBounds = boundsOf(floor);
    for (
      let x = Math.ceil(floorBounds.minX / gridFt) * gridFt;
      x <= floorBounds.maxX;
      x += gridFt
    ) {
      grid.push([
        { x, y: floorBounds.minY },
        { x, y: floorBounds.maxY },
      ]);
    }
    for (
      let y = Math.ceil(floorBounds.minY / gridFt) * gridFt;
      y <= floorBounds.maxY;
      y += gridFt
    ) {
      grid.push([
        { x: floorBounds.minX, y },
        { x: floorBounds.maxX, y },
      ]);
    }
  }

  return { floor, grid, solids, labels, bounds };
}

// ─── Rendering ──────────────────────────────────────────────────────────────

export interface RenderedFace {
  key: string;
  /** SVG `points` attribute. */
  points: string;
  fill: string;
  stroke: string;
  opacity: number;
  depth: number;
}

export interface RenderedLabel {
  key: string;
  text: string;
  x: number;
  y: number;
  depth: number;
}

export interface RenderedScene {
  floor: string;
  grid: Array<{ key: string; x1: number; y1: number; x2: number; y2: number }>;
  faces: RenderedFace[];
  labels: RenderedLabel[];
}

function toPointsAttr(points: Point2[]): string {
  return points.map((p) => `${round2(p.x)},${round2(p.y)}`).join(' ');
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

/**
 * Largest world span a single drawn polygon may cover, in feet.
 *
 * The painter's algorithm orders whole faces, and a face far longer than the
 * things around it has no single right place in that order: a 15ft head table
 * sorted by its own middle lands behind the chair at one end and in front of
 * the chair at the other, so one of them punches through it. Cutting big
 * surfaces into chair-sized pieces gives each piece a local answer, which is
 * the correct one. The seams are hidden by stroking each piece in its own
 * fill — the silhouette still reads, because top and side faces are lit
 * differently.
 */
const MAX_FACE_SPAN_FT = 3;
const MAX_FACE_DIVISIONS = 24;

function lerp3(a: Point3, b: Point3, t: number): Point3 {
  return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t, z: a.z + (b.z - a.z) * t };
}

function distance3(a: Point3, b: Point3): number {
  return Math.hypot(b.x - a.x, b.y - a.y, b.z - a.z);
}

function divisionsFor(lengthA: number, lengthB: number): number {
  const needed = Math.ceil(Math.max(lengthA, lengthB) / MAX_FACE_SPAN_FT);
  return Math.min(MAX_FACE_DIVISIONS, Math.max(1, needed));
}

/**
 * Split a quad into a grid of smaller quads. Anything that is not a quad —
 * a round table top, a hexagon — is left alone: those are small enough that
 * their own extent never spans the objects around them.
 */
export function subdivideFace(points: Point3[]): Point3[][] {
  if (points.length !== 4) return [points];
  const [a, b, c, d] = points;
  const cols = divisionsFor(distance3(a, b), distance3(d, c));
  const rows = divisionsFor(distance3(b, c), distance3(a, d));
  if (cols === 1 && rows === 1) return [points];
  const at = (u: number, v: number): Point3 => lerp3(lerp3(a, b, u), lerp3(d, c, u), v);
  const pieces: Point3[][] = [];
  for (let r = 0; r < rows; r++) {
    for (let col = 0; col < cols; col++) {
      const u0 = col / cols;
      const u1 = (col + 1) / cols;
      const v0 = r / rows;
      const v1 = (r + 1) / rows;
      pieces.push([at(u0, v0), at(u1, v0), at(u1, v1), at(u0, v1)]);
    }
  }
  return pieces;
}

/**
 * Project, cull and depth-sort a scene for one camera.
 *
 * Hidden surfaces are resolved by the painter's algorithm — faces are drawn
 * farthest-first — after back-face culling drops the ones pointing away. That
 * is enough for a floor plan, where solids rest on one plane and rarely
 * interpenetrate, and it keeps the whole renderer inside SVG.
 */
export function renderScene(scene: Scene3D, camera: Camera3D, viewport: Viewport): RenderedScene {
  const eye = eyePosition(camera);
  const faces: RenderedFace[] = [];

  scene.solids.forEach((solid) => {
    solidToFaces(solid, eye).forEach((face) => {
      if (face.points.length < 3) return;
      // Back-face cull once per face: it is visible when its outward normal
      // points at the eye, which in camera space sits at (0, 0, -distance).
      const camCorners = face.points.map((p) => toCameraSpace(p, camera));
      const normal = cross(
        subtract(camCorners[1] as Point3, camCorners[0] as Point3),
        subtract(camCorners[2] as Point3, camCorners[0] as Point3),
      );
      const toEye = subtract({ x: 0, y: 0, z: -camera.distanceFt }, camCorners[0] as Point3);
      if (dot(normal, toEye) <= 0) return;

      const worldNormal = cross(
        subtract(face.points[1], face.points[0]),
        subtract(face.points[2], face.points[0]),
      );
      const fill = shadeFace(face.color, faceBrightness(worldNormal));
      const pieces = subdivideFace(face.points);
      // A face drawn whole keeps its outline; the pieces of a split one are
      // stroked in their own fill so no grid appears across the surface.
      const stroke = pieces.length > 1 ? fill : shadeHexColor(fill, -22);

      pieces.forEach((piece, i) => {
        const camPoints = piece.map((p) => toCameraSpace(p, camera));
        const projected = camPoints.map((p) => projectCameraPoint(p, camera, viewport));
        if (projected.some((p) => p.behind)) return;
        faces.push({
          key: pieces.length > 1 ? `${face.key}#${i}` : face.key,
          points: toPointsAttr(projected),
          fill,
          stroke,
          opacity: face.opacity,
          depth: camPoints.reduce((sum, p) => sum + p.z, 0) / camPoints.length,
        });
      });
    });
  });

  faces.sort((a, b) => b.depth - a.depth);

  const floor = toPointsAttr(
    scene.floor.map((p) => project({ x: p.x, y: p.y, z: 0 }, camera, viewport)),
  );

  const grid = scene.grid.map(([a, b], i) => {
    const pa = project({ x: a.x, y: a.y, z: 0 }, camera, viewport);
    const pb = project({ x: b.x, y: b.y, z: 0 }, camera, viewport);
    return {
      key: `grid${i}`,
      x1: round2(pa.x),
      y1: round2(pa.y),
      x2: round2(pb.x),
      y2: round2(pb.y),
    };
  });

  const labels = scene.labels
    .map((label) => {
      const p = project(label.anchor, camera, viewport);
      return { key: label.key, text: label.text, x: round2(p.x), y: round2(p.y), depth: p.depth };
    })
    .filter((label) => Number.isFinite(label.x) && Number.isFinite(label.y))
    .sort((a, b) => b.depth - a.depth);

  return { floor, grid, faces, labels };
}

/** Headroom assumed when the caller passes bounds with no height of their own. */
const TALL_OBJECT_ALLOWANCE_FT = 10;
const FIT_MARGIN = 1.1;

/**
 * A camera framing the whole scene inside `viewport`.
 *
 * The frame has to follow the angle, not just the plan: turning the room swaps
 * which side spans the screen, tilting toward eye level squashes its depth to
 * almost nothing, and perspective magnifies whatever is nearest. Rather than
 * approximate all three, this projects the plan's bounding box through the
 * very camera being built and scales to what comes back — `pxPerFt` is a plain
 * multiplier on the projected position, so measuring at 1 and dividing is
 * exact for both projections.
 */
export function fitCamera(
  bounds: SceneBounds,
  viewport: Viewport,
  overrides: Partial<Camera3D> = {},
): Camera3D {
  const widthFt = Math.max(bounds.maxX - bounds.minX, 1);
  const depthFt = Math.max(bounds.maxY - bounds.minY, 1);
  const heightFt = Math.max(bounds.maxZ ?? TALL_OBJECT_ALLOWANCE_FT, 1);
  const camera: Camera3D = {
    ...DEFAULT_CAMERA,
    target: { x: (bounds.minX + bounds.maxX) / 2, y: (bounds.minY + bounds.maxY) / 2 },
    // Height counts toward the eye distance too, or a tall object in a small
    // room stands past the camera and its faces are culled away.
    distanceFt: Math.max(widthFt, depthFt, heightFt * 2) * 1.6,
    pxPerFt: 1,
    ...overrides,
  };
  // Measured against a unit scale — the fit is what sets pxPerFt, and a
  // caller's zoom multiplies the result rather than feeding into it.
  const probe: Camera3D = { ...camera, pxPerFt: 1 };

  const corners: Point3[] = [];
  [bounds.minX, bounds.maxX].forEach((x) =>
    [bounds.minY, bounds.maxY].forEach((y) =>
      [0, heightFt].forEach((z) => corners.push({ x, y, z })),
    ),
  );
  let halfWidth = 0;
  let halfHeight = 0;
  corners.forEach((corner) => {
    const p = project(corner, probe, viewport);
    halfWidth = Math.max(halfWidth, Math.abs(p.x - viewport.width / 2));
    halfHeight = Math.max(halfHeight, Math.abs(p.y - viewport.height / 2));
  });

  const pxPerFt = Math.min(
    viewport.width / 2 / (halfWidth * FIT_MARGIN),
    viewport.height / 2 / (halfHeight * FIT_MARGIN),
  );
  return {
    ...camera,
    pxPerFt: Number.isFinite(pxPerFt) && pxPerFt > 0 ? pxPerFt : 1,
  };
}

/** Named viewpoints offered in the 3D view's toolbar. */
export const CAMERA_PRESETS: Array<{ id: string; label: string; yaw: number; pitch: number }> = [
  { id: 'front', label: 'Front', yaw: 0, pitch: 22 },
  { id: 'corner', label: 'Corner', yaw: 38, pitch: 42 },
  { id: 'side', label: 'Side', yaw: 90, pitch: 30 },
  { id: 'rear', label: 'Rear', yaw: 180, pitch: 34 },
  { id: 'top', label: 'Top', yaw: 0, pitch: 90 },
];
